<template>
  <transition
    :enterActiveClass="$style.slideTransition"
    :enterClass="$style.slideEnter"
    :leaveActiveClass="$style.slideTransition"
    :leaveToClass="$style.slideLeaveTo"
  >
    <div
      v-if="visible"
      :class="{[$style.container]: true, [$style.offsetTop]: offsetTop, }"
    >
      <CollapsableSection
        :class="$style.collapsableSection"
        :collapse="collapsed"
        :showCaret="false"
        :title="displayTitle"
        @toggleCollapse="collapsed => toggleCollapse(null, collapsed)"
      >
        <div
          v-if="hasDivContainerSlot"
          :class="$style.stepsContainer"
        >
          <slot name="divcontainer" />
        </div>
        <ul
          v-else
          :class="$style.stepsContainer"
        >
          <slot />
        </ul>
        <div
          v-if="buttonContainerVisible"
          :class="$style.buttonContainer"
        >
          <FlatActionAnchor
            v-if="primaryLink"
            :class="$style.learnMoreLink"
            :href="primaryLink"
            :text="primaryLinkText"
            @click="trackLinkClicked(title, primaryLinkText, primaryLink, 'primary link')"
          />
          <button
            v-if="videoInModal"
            :class="[$style.link, $style.secondaryLink, $style.modalButton]"
            @click="showModal"
          >
            {{ secondaryLinkText }}
          </button>
          <a
            v-else-if="secondaryLink"
            :class="[$style.link, $style.secondaryLink]"
            :href="secondaryLink"
            rel="noopener noreferrer"
            target="_blank"
            @click="trackLinkClicked(title, secondaryLinkText, secondaryLink, 'secondary link')"
          >
            {{ secondaryLinkText }}
          </a>
        </div>
      </CollapsableSection>
      <div :class="$style.dismissButtonContainer">
        <button
          :class="[$style.link, $style.collapseLink]"
          @click="toggleCollapse"
        >
          {{ collapsed ? 'expand' : 'collapse' }}
        </button>
        <FlatActionButton
          v-if="showDismiss"
          :action="handleDismiss"
          :buttonClass="dismissButtonClass"
        >
          {{ dismissButtonText }}
        </FlatActionButton>
      </div>
    </div>
  </transition>
</template>

<script>
import CollapsableSection from 'components/ContentSection/CollapsableSection.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionAnchor from 'components/Buttons/FlatActionAnchor.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';
import LocalStorageService from 'client/util/LocalStorageService';
import ModalEventBus from 'components/Modal/ModalEventBus';
import VideoModal from 'client/components/Modal/VideoModal.vue';

export default {
  name: 'GetStartedInstructions',

  components: {
    CollapsableSection,
    FlatActionAnchor,
    FlatActionButton,
  },

  LocalStorageService,

  props: {
    dismissButtonClass: {
      type: [String, Array],
      default: 'btnDefault',
    },
    dismissButtonText: {
      type: String,
      default: 'got it',
    },
    localStorageKey: {
      type: String,
      default: '',
    },
    offsetTop: {
      type: Boolean,
    },
    overrideLocalStorageCollapseSetting: {
      type: Boolean,
      default: false,
    },
    primaryLink: {
      type: String,
      default: '',
    },
    primaryLinkText: {
      type: String,
      default: 'learn more',
    },
    secondaryLink: {
      type: String,
      default: '',
    },
    secondaryLinkText: {
      type: String,
      default: '',
    },
    showDismiss: {
      type: Boolean,
      default: true,
    },
    showTitleWhenExpanded: {
      type: Boolean,
      default: true,
    },
    startCollapsed: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    videoModalUrls: {
      type: Object,
      default: () => {},
    },
  },

  data() {
    return {
      collapsed: !this.overrideLocalStorageCollapseSetting && (this.startCollapsed || this.$options.LocalStorageService.getItem(this.localStorageKey) === 'collapsed'),
      visible: this.$options.LocalStorageService.getItem(this.localStorageKey) !== 'dismissed',
    };
  },

  computed: {
    buttonContainerVisible() {
      return !!this.secondaryLink || !!this.primaryLink || !!this.videoInModal;
    },
    displayTitle() {
      let newTitle = this.title;
      if (!this.showTitleWhenExpanded && !this.collapsed) {
        newTitle = '';
      }
      return newTitle;
    },
    hasDivContainerSlot() {
      return !!this.$slots.divcontainer;
    },
    videoInModal() {
      return this.videoModalUrls
      && this.videoModalUrls.videoIframeUrl
      && this.videoModalUrls.videoUrl;
    },
  },

  watch: {
    startCollapsed(startCollapsedNewValue) {
      if (startCollapsedNewValue) {
        this.collapsed = true;
        this.$options.LocalStorageService.setItem(this.localStorageKey, 'collapsed');
      }
    },
  },

  methods: {
    handleDismiss() {
      this.visible = false;
      this.$options.LocalStorageService.setItem(this.localStorageKey, 'dismissed');
      this.trackCollapsableSectionControlEvent(this.title, 'dismissed');
    },

    showModal() {
      const kbLinkObject = {
        action: () => EventHelper.triggerVideoModalLinkClicked({
          text: this.primaryLinkText,
          url: this.primaryLink,
        }),
        url: this.primaryLink,
        text: this.primaryLinkText,
      };
      this.trackButtonClicked(this.title, this.secondaryLinkText);
      ModalEventBus.open({
        component: VideoModal,
        isLargeModal: true,
        componentProps: {
          videos: [
            {
              iframeUrl: this.videoModalUrls.videoIframeUrl,
              kbLinkObject,
              videoUrl: this.videoModalUrls.videoUrl,
            },
          ],
        },
      });
    },

    toggleCollapse(ev, collapsedEventValue) {
      this.collapsed = collapsedEventValue || !this.collapsed;
      if (this.collapsed) {
        this.trackCollapsableSectionControlEvent(this.title, 'collapsed');
        this.$options.LocalStorageService.setItem(this.localStorageKey, 'collapsed');
      } else {
        this.trackCollapsableSectionControlEvent(this.title, 'expanded');
        this.$options.LocalStorageService.setItem(this.localStorageKey, 'expanded');
      }
    },

    trackButtonClicked(title, text) {
      EventHelper.triggerCollapsableSectionButtonClicked({
        sectionTitle: title,
        buttonText: text,
      });
    },

    trackCollapsableSectionControlEvent(title, action) {
      EventHelper.triggerCollapsableSectionControlEvent({
        sectionTitle: title,
        action,
      });
    },

    trackLinkClicked(title, text, url, type) {
      EventHelper.triggerCollapsableSectionLinkClicked({
        sectionTitle: title,
        text,
        url,
        type,
      });
    },
  },
};
</script>

<style module>
@value (max-width-small) from '~css/breakpoints.css';
@value (allTransition) from '~css/transitions.css';

.collapsableSection {
  min-height: 60px;
}

.container {
  font-size: 1rem;
  margin: 0 var(--asideSpacing) var(--asideSpacing);
  max-height: 1000px;
  position: relative;
}

@media (max-width: max-width-small) {
  .container {
    display: none;
  }
}

.offsetTop {
  margin-top: 64px;
}

.sectionContainer {
  position: relative;
}

.link {
  background: none;
  color: inherit !important;
  cursor: pointer;
  padding: 0;
  text-decoration: underline !important;
}

ul.stepsContainer {
  display: flex;
  list-style-type: none;
  margin: 0;
  padding: 0;
}

.buttonContainer {
  align-items: center;
  display: flex;
  justify-content: center;
  margin-top: 25px;
  text-align: center;
}

.secondaryLink {
  margin-left: 15px;
}

.modalButton {
  background-color: transparent;
}

.dismissButtonContainer {
  align-items: center;
  display: flex;
  position: absolute;
  right: 40px;
  top: 18px;
}

.collapseLink.collapseLink {
  margin: 0 15px;
}

.slideEnter,
.slideLeaveTo {
  max-height: 0;
}

.slideTransition {
  transition: allTransition;
}
</style>
